import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Clock } from 'lucide-react';

// 1 hour total inactivity, with a 60-second warning before forced logout.
const INACTIVITY_MS = 60 * 60 * 1000;
const WARNING_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

const InactivityMonitor = () => {
  const { isAuthenticated, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSecs, setRemainingSecs] = useState(Math.floor(WARNING_MS / 1000));

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const showWarningRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const performLogout = useCallback(async () => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    try {
      sessionStorage.setItem('inactivity_logout', '1');
    } catch (e) {
      // ignore storage errors
    }
    await logout();
  }, [logout, clearAllTimers]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      showWarningRef.current = true;
      setShowWarning(true);
      setRemainingSecs(Math.floor(WARNING_MS / 1000));

      countdownRef.current = setInterval(() => {
        setRemainingSecs((s) => (s > 0 ? s - 1 : 0));
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        performLogout();
      }, WARNING_MS);
    }, INACTIVITY_MS - WARNING_MS);
  }, [clearAllTimers, performLogout]);

  const handleActivity = useCallback(() => {
    // Once the warning modal is up we ignore implicit activity — the user
    // must explicitly click "Stay signed in" or "Sign Out".
    if (showWarningRef.current) return;
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      return undefined;
    }

    startTimers();
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
    };
  }, [isAuthenticated, handleActivity, startTimers, clearAllTimers]);

  const handleStayLoggedIn = () => {
    startTimers();
  };

  if (!isAuthenticated || !showWarning) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="inactivity-warning-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-modal-title"
    >
      <div className="bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
            <Clock className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 id="inactivity-modal-title" className="text-xl font-bold text-white">Still there?</h3>
            <p className="text-sm text-slate-400">Your session is about to expire.</p>
          </div>
        </div>
        <p className="text-slate-300 text-sm">
          For your security, you'll be signed out due to inactivity in:
        </p>
        <div className="text-center my-5">
          <div
            className="text-5xl font-bold text-amber-400 tabular-nums"
            data-testid="inactivity-countdown"
          >
            {remainingSecs}s
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <Button
            onClick={performLogout}
            variant="outline"
            className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="inactivity-signout-btn"
          >
            Sign Out
          </Button>
          <Button
            onClick={handleStayLoggedIn}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="inactivity-stay-btn"
          >
            Stay signed in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InactivityMonitor;
